import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import api from '../../api';

const PERIODS = [
  { key: 'daily', label: 'Hoy' },
  { key: 'weekly', label: 'Semana' },
  { key: 'monthly', label: 'Mes' },
  { key: 'yearly', label: 'Año' },
  { key: 'custom', label: 'Rango' },
];

export default function Revenue() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [period, setPeriod] = useState('monthly');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showCashModal, setShowCashModal] = useState(false);
  const [cashAmount, setCashAmount] = useState('');
  const [cashDescription, setCashDescription] = useState('');
  const [cashPaymentMethod, setCashPaymentMethod] = useState('efectivo');
  const [savingCash, setSavingCash] = useState(false);

  useEffect(() => { if (user?.id) fetchRevenue(); }, [period, fromDate, toDate, user?.id]);

  const fetchRevenue = async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === 'custom' && fromDate && toDate) { params.from_date = fromDate; params.to_date = toDate; }
      const r = await api.get(`/analytics/revenue/${user.id}`, { params });
      setData(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleCashEntry = async () => {
    const amt = parseFloat(cashAmount);
    if (isNaN(amt) || amt <= 0) { alert('Ingresa un monto válido'); return; }
    if (!cashPaymentMethod) { alert('Selecciona Efectivo o Transferencia'); return; }
    setSavingCash(true);
    try {
      await api.post('/cash-entries', { business_user_id: user.id, amount: amt, description: cashDescription || 'Ingreso de caja', payment_method: cashPaymentMethod });
      setCashAmount(''); setCashDescription(''); setCashPaymentMethod('efectivo'); setShowCashModal(false);
      fetchRevenue(); alert('Ingreso de caja registrado');
    } catch (e) { alert('Error'); }
    finally { setSavingCash(false); }
  };

  const fmt = (n) => `$${(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

  return (
    <div className="page-full">
      <div className="header">
        <button className="icon-btn" onClick={() => navigate(-1)}>←</button>
        <h1 className="header-title" style={{ flex: 1 }}>Ganancias</h1>
        <button className="cash-entry-btn" onClick={() => setShowCashModal(true)}>💵 Ingreso Caja</button>
      </div>
      <div className="content-scroll">
        <div className="period-tabs">
          {PERIODS.map(p => (
            <button key={p.key} className={`period-tab ${period === p.key ? 'active' : ''}`}
              onClick={() => setPeriod(p.key)}>{p.label}</button>
          ))}
        </div>

        {period === 'custom' && (
          <div className="date-range-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label className="label">Desde</label>
              <input className="input" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="label">Hasta</label>
              <input className="input" type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
            </div>
          </div>
        )}

        {loading ? <div className="spinner" style={{ marginTop: '40px' }} /> : data && (
          <>
            <div className="stats-grid">
              <div className="stat-card" style={{ borderLeftColor: 'var(--accent)' }}
                onClick={() => navigate(`/business/revenue-detail?period=${period}&fromDate=${fromDate}&toDate=${toDate}`)}>
                <div className="stat-label">Ingresos Totales</div>
                <div className="stat-value accent-text">{fmt(data.total_revenue)}</div>
                <span className="stat-arrow">›</span>
              </div>
              <div className="stat-card" style={{ borderLeftColor: '#6C63FF' }}
                onClick={() => navigate(`/business/sales-list?period=${period}&fromDate=${fromDate}&toDate=${toDate}`)}>
                <div className="stat-label">Ventas</div>
                <div className="stat-value" style={{ color: '#6C63FF' }}>{data.total_sales}</div>
                <span className="stat-arrow">›</span>
              </div>
              <div className="stat-card" style={{ borderLeftColor: '#E53935' }}
                onClick={() => navigate('/business/expenses')}>
                <div className="stat-label">Gastos</div>
                <div className="stat-value" style={{ color: '#E53935' }}>{fmt(data.total_expenses)}</div>
                <span className="stat-arrow">›</span>
              </div>
              <div className="stat-card" style={{ borderLeftColor: '#4CAF50' }}>
                <div className="stat-label">Ganancia Neta</div>
                <div className="stat-value" style={{ color: '#4CAF50' }}>{fmt((data.net_profit || 0) + (data.cash_entries_total || 0))}</div>
                <div className="stat-sub">Ventas + caja - gastos</div>
              </div>
              <div className="stat-card" style={{ borderLeftColor: '#2196F3' }}>
                <div className="stat-label">Ganancia Neta Ventas</div>
                <div className="stat-value" style={{ color: '#2196F3' }}>{fmt(data.total_profit)}</div>
                <div className="stat-sub">Solo ganancias de ventas</div>
              </div>
              <div className="stat-card" style={{ borderLeftColor: '#FF9800' }}
                onClick={() => navigate('/business/debts')}>
                <div className="stat-label">No Cobrado</div>
                <div className="stat-value" style={{ color: '#FF9800' }}>{fmt(data.unpaid_amount)}</div>
                <span className="stat-arrow">›</span>
              </div>
              {(data.provider_payments_total || 0) > 0 && (
                <div className="stat-card" data-testid="provider-payments-card" style={{ borderLeftColor: '#27AE60' }}
                  onClick={() => navigate('/business/purchases')}>
                  <div className="stat-label">Pagos a Proveedores</div>
                  <div className="stat-value" style={{ color: '#27AE60' }}>{fmt(data.provider_payments_total)}</div>
                  <div className="stat-sub">No afecta la Ganancia Neta</div>
                  <span className="stat-arrow">›</span>
                </div>
              )}
              {(data.cash_entries_total || 0) > 0 && (
                <div className="stat-card" style={{ borderLeftColor: '#9C27B0' }}>
                  <div className="stat-label">Ingreso Caja</div>
                  <div className="stat-value" style={{ color: '#9C27B0' }}>{fmt(data.cash_entries_total)}</div>
                </div>
              )}
            </div>

            <div className="payment-methods-card">
              <h3 style={{ marginBottom: '12px', fontWeight: '700' }}>Medios de Pago</h3>
              <div style={{ display: 'flex' }}>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: '20px' }}>📱</div>
                  <div className="card-row-detail">Transferencia</div>
                  <div style={{ fontSize: '18px', fontWeight: '700' }}>{fmt(data.transfer_total || 0)}</div>
                  {(data.cash_entries_transfer || 0) > 0 && (
                    <div style={{ fontSize: '0.7rem', color: 'var(--text2)', marginTop: 2 }}>
                      incl. caja {fmt(data.cash_entries_transfer)}
                    </div>
                  )}
                </div>
                <div style={{ width: '1px', background: 'var(--border)' }} />
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: '20px' }}>💵</div>
                  <div className="card-row-detail">Efectivo</div>
                  <div style={{ fontSize: '18px', fontWeight: '700' }}>{fmt(data.cash_total || 0)}</div>
                  {(data.cash_entries_efectivo || 0) > 0 && (
                    <div style={{ fontSize: '0.7rem', color: 'var(--text2)', marginTop: 2 }}>
                      incl. caja {fmt(data.cash_entries_efectivo)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {showCashModal && (
        <div className="modal-overlay" onClick={() => setShowCashModal(false)}>
          <div className="modal-content modal-center" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Ingreso de Caja</h3>
            <p className="card-row-detail" style={{ textAlign: 'center', marginBottom: '12px' }}>Registra un monto que no está en las ventas</p>
            <div className="form-group"><input className="input" placeholder="Monto ($)" type="number" step="0.01" value={cashAmount} onChange={e => setCashAmount(e.target.value)} /></div>
            <div className="form-group"><input className="input" placeholder="Descripción (opcional)" value={cashDescription} onChange={e => setCashDescription(e.target.value)} /></div>
            <div className="form-group">
              <label className="label">Medio de Pago *</label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button data-testid="cash-entry-efectivo"
                  className={cashPaymentMethod === 'efectivo' ? 'btn-primary' : 'btn-secondary'}
                  style={{ flex: 1, padding: '10px' }}
                  onClick={() => setCashPaymentMethod('efectivo')}>💵 Efectivo</button>
                <button data-testid="cash-entry-transferencia"
                  className={cashPaymentMethod === 'transferencia' ? 'btn-primary' : 'btn-secondary'}
                  style={{ flex: 1, padding: '10px' }}
                  onClick={() => setCashPaymentMethod('transferencia')}>📱 Transferencia</button>
              </div>
            </div>
            <div className="modal-buttons">
              <button className="btn-secondary" onClick={() => setShowCashModal(false)}>Cancelar</button>
              <button className="btn-primary" onClick={handleCashEntry} disabled={savingCash}>{savingCash ? 'Guardando...' : 'Registrar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
